// src/api/payoutsApi.js
import { getAxiosInstance } from "../utils/apiClient";
import { normalizeUsername } from "../utils/normalizeUsername";
import { getTokens } from "../utils/tokens";

const axiosInstance = getAxiosInstance();

/** ===================== Creator ===================== **/

// GET /api/payouts/balance — доступний баланс/резерв/виплачене
export const getPayoutBalanceAPI = () =>
  new Promise((resolve, reject) => {
    const tokens = getTokens();
    axiosInstance
      .get("/api/payouts/balance", {
        headers: {
          "Content-Type": "application/json",
          [process.env.REACT_APP_AUTH_TOKEN]: `Bearer ${tokens.access_token}`,
          [process.env.REACT_APP_FIREBASE_AUTH_TOKEN]:
            `Bearer ${tokens.firebase_token}`,
          "ngrok-skip-browser-warning": "true",
        },
      })
      .then((res) => resolve(res.data))
      .catch((err) => reject(err?.response?.data || err));
  });

// GET /api/payouts — мої заявки на виплати (пагінація)
export const getMyPayoutsAPI = ({ page = 1, limit = 20 } = {}) =>
  new Promise((resolve, reject) => {
    const tokens = getTokens();
    axiosInstance
      .get("/api/payouts", {
        headers: {
          "Content-Type": "application/json",
          [process.env.REACT_APP_AUTH_TOKEN]: `Bearer ${tokens.access_token}`,
          [process.env.REACT_APP_FIREBASE_AUTH_TOKEN]:
            `Bearer ${tokens.firebase_token}`,
          "ngrok-skip-browser-warning": "true",
        },
        params: { page, limit },
      })
      .then((res) => resolve(res.data))
      .catch((err) => reject(err?.response?.data || err));
  });

// POST /api/payouts/request — створити заявку (вимагатиме податкової форми, якщо ввімкнено middleware)
export const requestPayoutAPI = ({ amount, method, destination, note } = {}) =>
  new Promise((resolve, reject) => {
    const tokens = getTokens();
    axiosInstance
      .post(
        "/api/payouts/request",
        { amount, method, destination, note },
        {
          headers: {
            "Content-Type": "application/json",
            [process.env.REACT_APP_AUTH_TOKEN]: `Bearer ${tokens.access_token}`,
            [process.env.REACT_APP_FIREBASE_AUTH_TOKEN]:
              `Bearer ${tokens.firebase_token}`,
            "ngrok-skip-browser-warning": "true",
          },
        },
      )
      .then((res) => resolve(res.data))
      .catch((err) => reject(err?.response?.data || err));
  });

/** ===================== Admin ===================== **/

// GET /api/payouts/admin — список заявок (фільтри + пагінація)
export const adminGetPayoutsAPI = ({
  status,
  creatorId,
  username,
  page = 1,
  limit = 50,
  sortBy = "createdAt",
  sortDir = "desc",
} = {}) =>
  new Promise((resolve, reject) => {
    const tokens = getTokens();

    axiosInstance
      .get("/api/payouts/admin", {
        headers: {
          "Content-Type": "application/json",
          [process.env.REACT_APP_AUTH_TOKEN]: `Bearer ${tokens.access_token}`,
          [process.env.REACT_APP_FIREBASE_AUTH_TOKEN]:
            `Bearer ${tokens.firebase_token}`,
          "ngrok-skip-browser-warning": "true",
        },
        params: {
          status, creatorId, username: normalizeUsername(username), page, limit, sortBy, sortDir,
        },
      })
      .then((res) => resolve(res.data))
      .catch((err) => reject(err?.response?.data || err));
  });

// GET /api/payouts/admin/:id — деталі однієї виплати (включно з повними реквізитами)
export const adminGetPayoutDetailsAPI = (id) =>
  new Promise((resolve, reject) => {
    const tokens = getTokens();
    axiosInstance
      .get(`/api/payouts/admin/${id}`, {
        headers: {
          "Content-Type": "application/json",
          [process.env.REACT_APP_AUTH_TOKEN]: `Bearer ${tokens.access_token}`,
          [process.env.REACT_APP_FIREBASE_AUTH_TOKEN]:
            `Bearer ${tokens.firebase_token}`,
          "ngrok-skip-browser-warning": "true",
        },
      })
      .then((res) => resolve(res.data))
      .catch((err) => reject(err?.response?.data || err));
  });

// POST /api/payouts/admin/:id/approve — схвалити
export const adminApprovePayoutAPI = (id) =>
  new Promise((resolve, reject) => {
    const tokens = getTokens();
    axiosInstance
      .post(
        `/api/payouts/admin/${id}/approve`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            [process.env.REACT_APP_AUTH_TOKEN]: `Bearer ${tokens.access_token}`,
            [process.env.REACT_APP_FIREBASE_AUTH_TOKEN]:
              `Bearer ${tokens.firebase_token}`,
            "ngrok-skip-browser-warning": "true",
          },
        },
      )
      .then((res) => resolve(res.data))
      .catch((err) => reject(err?.response?.data || err));
  });

// POST /api/payouts/admin/:id/mark-paid — позначити як виплачену
export const adminMarkPaidPayoutAPI = (id, { providerRef, note } = {}) =>
  new Promise((resolve, reject) => {
    const tokens = getTokens();
    axiosInstance
      .post(
        `/api/payouts/admin/${id}/mark-paid`,
        { providerRef, note },
        {
          headers: {
            "Content-Type": "application/json",
            [process.env.REACT_APP_AUTH_TOKEN]: `Bearer ${tokens.access_token}`,
            [process.env.REACT_APP_FIREBASE_AUTH_TOKEN]:
              `Bearer ${tokens.firebase_token}`,
            "ngrok-skip-browser-warning": "true",
          },
        },
      )
      .then((res) => resolve(res.data))
      .catch((err) => reject(err?.response?.data || err));
  });

// POST /api/payouts/admin/:id/reject — відхилити
export const adminRejectPayoutAPI = (id, { note } = {}) =>
  new Promise((resolve, reject) => {
    const tokens = getTokens();
    axiosInstance
      .post(
        `/api/payouts/admin/${id}/reject`,
        { note },
        {
          headers: {
            "Content-Type": "application/json",
            [process.env.REACT_APP_AUTH_TOKEN]: `Bearer ${tokens.access_token}`,
            [process.env.REACT_APP_FIREBASE_AUTH_TOKEN]:
              `Bearer ${tokens.firebase_token}`,
            "ngrok-skip-browser-warning": "true",
          },
        },
      )
      .then((res) => resolve(res.data))
      .catch((err) => reject(err?.response?.data || err));
  });

// POST /api/payouts/admin/:id/receipt — завантажити квитанцію (multipart/form-data, field: file)
export const adminUploadPayoutReceiptAPI = (id, file) =>
  new Promise((resolve, reject) => {
    const tokens = getTokens();
    const formData = new FormData();
    formData.append("file", file); // поле name має бути "file"

    axiosInstance
      .post(`/api/payouts/admin/${id}/receipt`, formData, {
        headers: {
          // НЕ виставляємо вручну Content-Type — браузер сам поставить boundary
          [process.env.REACT_APP_AUTH_TOKEN]: `Bearer ${tokens.access_token}`,
          [process.env.REACT_APP_FIREBASE_AUTH_TOKEN]:
            `Bearer ${tokens.firebase_token}`,
          "ngrok-skip-browser-warning": "true",
        },
      })
      .then((res) => resolve(res.data))
      .catch((err) => reject(err?.response?.data || err));
  });

// GET /api/payouts/admin/export.csv — експорт payout'ів у CSV (фільтри через query)
export const adminExportPayoutsCSVAPI = ({
  status,
  creatorId,
  method,
  from,
  to,
} = {}) =>
  new Promise((resolve, reject) => {
    const tokens = getTokens();
    axiosInstance
      .get("/api/payouts/admin/export.csv", {
        headers: {
          [process.env.REACT_APP_AUTH_TOKEN]: `Bearer ${tokens.access_token}`,
          [process.env.REACT_APP_FIREBASE_AUTH_TOKEN]:
            `Bearer ${tokens.firebase_token}`,
          "ngrok-skip-browser-warning": "true",
        },
        params: { status, creatorId, method, from, to },
        responseType: "blob", // важливо для файлу
      })
      .then((res) => resolve(res.data)) // вертаємо Blob
      .catch((err) => reject(err?.response?.data || err));
  });
// --- деталі payout (повні реквізити) ---
// export const adminGetPayoutDetailsAPI = (id) => {
//     return new Promise((resolve, reject) => {
//         const tokens = getTokens();
//         axiosInstance
//             .get(`/api/payouts/admin/${id}`, {
//                 headers: {
//                     "Content-Type": "application/json",
//                     [process.env.REACT_APP_AUTH_TOKEN]: `Bearer ${tokens.access_token}`,
//                     [process.env.REACT_APP_FIREBASE_AUTH_TOKEN]: `Bearer ${tokens.firebase_token}`,
//                     "ngrok-skip-browser-warning": "true",
//                 },
//             })
//             .then((res) => resolve(res.data))
//             .catch((err) => reject(err?.response?.data || err));
//     });
// };

// --- завантажити квитанцію (multipart/form-data) ---
export const adminUploadReceiptAPI = (id, file) => {
  return new Promise((resolve, reject) => {
    const tokens = getTokens();
    const form = new FormData();
    form.append("file", file);

    axiosInstance
      .post(`/api/payouts/admin/${id}/receipt`, form, {
        headers: {
          [process.env.REACT_APP_AUTH_TOKEN]: `Bearer ${tokens.access_token}`,
          [process.env.REACT_APP_FIREBASE_AUTH_TOKEN]:
            `Bearer ${tokens.firebase_token}`,
          "ngrok-skip-browser-warning": "true",
          // axios сам поставить boundary
        },
      })
      .then((res) => resolve(res.data))
      .catch((err) => reject(err?.response?.data || err));
  });
};

// --- export CSV (отримаємо blob і збережемо файл) ---
export const adminExportPayoutsCSV = ({ status, creatorId } = {}) => {
  return new Promise((resolve, reject) => {
    const tokens = getTokens();
    axiosInstance
      .get(`/api/payouts/admin/export.csv`, {
        responseType: "blob",
        headers: {
          [process.env.REACT_APP_AUTH_TOKEN]: `Bearer ${tokens.access_token}`,
          [process.env.REACT_APP_FIREBASE_AUTH_TOKEN]:
            `Bearer ${tokens.firebase_token}`,
          "ngrok-skip-browser-warning": "true",
        },
        params: { status, creatorId },
      })
      .then((res) => {
        const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `payouts_${Date.now()}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        resolve(true);
      })
      .catch((err) => reject(err?.response?.data || err));
  });
};
